/**
 * Слои единой карты как КОНФИГУРАЦИИ (agroai-6al.1, протокол —
 * `docs/rules/map-widget.md`).
 *
 * Слой — это не компонент и не ветка `if` в кадре карты, а запись реестра:
 * откуда берётся значение контура, каким ключом feature-state оно уходит на
 * полотно, какая у значения «тяжесть» (для точек и кластеров на отдалении) и
 * какой подписью оно читается в карточке контура. Цвета здесь только из
 * зеркала токенов `PALETTE` (ADR-0009): своих цветов у слоя нет и быть не может.
 *
 * Модуль чистый: ни React, ни MapLibre. Всё, что он решает, проверяется
 * тестами `layers.test.ts`.
 */

import { GREEN_RAMP, HEAT_RAMP, PALETTE } from '../styles/palette';
import type { QuarantineStatusValue } from '../data/types';
import { LEGEND_NONE, type ColorBy, type WorkStatusValue } from './paint';

/* ----------------------------------------------------------- тяжесть */

/**
 * Семантическая шкала состояния (ADR-0009): светофор плюс две нейтрали.
 * Красный — ТОЛЬКО критичное состояние объекта (красный карантин), никогда
 * «без отчёта» и не оценка людей.
 */
export type Severity = 'critical' | 'warning' | 'progress' | 'ok' | 'missing' | 'future';

/** Порядок «хуже → лучше»: кластер красится худшим из того, что в нём лежит. */
export const SEVERITY_ORDER: readonly Severity[] = [
  'critical',
  'warning',
  'progress',
  'missing',
  'ok',
  'future',
];

export const SEVERITY_COLOR: Record<Severity, string> = {
  critical: PALETTE.stAlert,
  warning: PALETTE.stWarn,
  progress: PALETTE.stProgress,
  ok: PALETTE.stOk,
  missing: PALETTE.stMissing,
  future: PALETTE.stFuture,
};

/** Тёмное кольцо точки: светлые ступени без края теряются на светлых заливках. */
export const SEVERITY_RING: Record<Severity, string> = {
  critical: PALETTE.stAlertInk,
  warning: PALETTE.stWarnInk,
  progress: PALETTE.stProgressInk,
  ok: PALETTE.stOkDeep,
  missing: PALETTE.muted,
  future: PALETTE.muted,
};

/** Тяжести, которые требуют внимания: им точки ставятся по умолчанию. */
export const ATTENTION: readonly Severity[] = ['critical', 'warning'];

/**
 * Какие контуры получают точку на отдалении.
 * `attention` — только критичные и предупреждения, `all` — все контуры с
 * тяжестью, `none` — точек нет.
 */
export type PointsMode = 'attention' | 'all' | 'none';

/* ------------------------------------------------- данные слоёв (пропсы) */

/** Состояние подачи воды на контур (из распределений `/water/works/{id}`). */
export type WaterStateValue = 'delivered' | 'partial' | 'none' | 'unmeasured';

/**
 * Тон значения произвольного слоя. Только имена семантической шкалы и
 * зелёной рампы: цвет страница не передаёт — передаёт смысл.
 */
export type DataTone =
  | Severity
  | 'water'
  | 'water-soft'
  | 'candidate'
  | 'ramp-1'
  | 'ramp-2'
  | 'ramp-3'
  | 'ramp-4'
  | 'ramp-5'
  | HeatTone
  | 'neutral';

/** Ступени шкалы частоты: одна краснота, светлое → тёмное (тепловая карта). */
export type HeatTone = 'heat-1' | 'heat-2' | 'heat-3' | 'heat-4' | 'heat-5';
export const HEAT_TONES: readonly HeatTone[] = ['heat-1', 'heat-2', 'heat-3', 'heat-4', 'heat-5'];

export const TONE_COLOR: Record<DataTone, string> = {
  ...SEVERITY_COLOR,
  water: PALETTE.aqua,
  'water-soft': PALETTE.aquaSoft,
  candidate: PALETTE.copperSoft,
  'ramp-1': GREEN_RAMP[0],
  'ramp-2': GREEN_RAMP[1],
  'ramp-3': GREEN_RAMP[2],
  'ramp-4': GREEN_RAMP[3],
  'ramp-5': GREEN_RAMP[4],
  'heat-1': HEAT_RAMP[0],
  'heat-2': HEAT_RAMP[1],
  'heat-3': HEAT_RAMP[2],
  'heat-4': HEAT_RAMP[3],
  'heat-5': HEAT_RAMP[4],
  neutral: PALETTE.track,
};

/** Обводка образца легенды и контура: смысл не держится одной заливкой. */
export const TONE_OUTLINE: Record<DataTone, string> = {
  ...SEVERITY_RING,
  water: PALETTE.aquaDeep,
  'water-soft': PALETTE.aqua,
  candidate: PALETTE.copperDeep,
  'ramp-1': PALETTE.lineStrong,
  'ramp-2': PALETTE.active,
  'ramp-3': PALETTE.active,
  'ramp-4': PALETTE.deep,
  'ramp-5': PALETTE.deep,
  'heat-1': HEAT_RAMP[2],
  'heat-2': HEAT_RAMP[3],
  'heat-3': HEAT_RAMP[4],
  'heat-4': HEAT_RAMP[4],
  'heat-5': PALETTE.ink,
  neutral: PALETTE.lineStrong,
};

/** Одно значение произвольного слоя: ключ данных, подпись, тон. */
export interface ValueLegendEntry {
  key: string;
  label: string;
  tone: DataTone;
  note?: string;
}

/**
 * Произвольный слой страницы (`colorBy="values"`): значения по контурам и
 * легенда к ним. Числа страница раскладывает по ступеням сама (`ramp-1..5`):
 * непрерывная шкала без названных порогов читалась бы как точность, которой
 * у данных нет.
 */
export interface ValuesLayer {
  title: string;
  byId: Record<string, string>;
  legend: ValueLegendEntry[];
  /** Подпись контура без значения; по умолчанию «нет значения». */
  noneLabel?: string;
}

/**
 * Данные слоёв, которые передаёт страница. Геометрию кадр берёт сам
 * (`useContourGeo`), страница отдаёт только значения по id контура.
 */
export interface MapLayerData {
  workStatusById?: Record<string, WorkStatusValue>;
  quarantineById?: Record<string, QuarantineStatusValue>;
  waterById?: Record<string, WaterStateValue>;
  values?: ValuesLayer;
}

/* ------------------------------------------------------ реестр слоёв */

/** Свойства фичи, которые нужны слоям (подмножество атрибутов контура). */
export interface LayerFeatureProps {
  id: string;
  crop_2026?: unknown;
  category?: unknown;
  geometry_status?: unknown;
  [key: string]: unknown;
}

/** Ключ feature-state, которым значение слоя уходит на полотно. */
export type StateKey = 'ws' | 'q' | 'v';

export interface LayerSpec {
  id: ColorBy;
  /** Ключ feature-state; `null` — слой красит по свойству фичи. */
  stateKey: StateKey | null;
  /** Каких данных страницы слою не хватает; `null` — слою хватает геометрии. */
  requires: keyof MapLayerData | null;
  /** Значение контура — ключ легенды; `LEGEND_NONE`, если значения нет. */
  valueOf: (props: LayerFeatureProps, data: MapLayerData) => string;
  /** Тяжесть значения — для точек на отдалении; `null` — точка не нужна. */
  severity: (key: string, data: MapLayerData) => Severity | null;
  /** Точки по умолчанию для слоя. */
  points: PointsMode;
  /** Значения слоя по id для feature-state (только слои с `stateKey`). */
  stateById?: (data: MapLayerData) => Record<string, string> | undefined;
}

const str = (value: unknown): string | null =>
  value === null || value === undefined || value === '' ? null : String(value);

const WORK_SEVERITY: Record<WorkStatusValue, Severity> = {
  reported_verified: 'ok',
  reported: 'ok',
  in_progress: 'ok',
  in_window: 'progress',
  upcoming: 'future',
  // «Без отчёта» — про данные, а не про работу в поле: нейтраль, не красный.
  silent: 'missing',
};

const QUARANTINE_SEVERITY: Record<QuarantineStatusValue, Severity> = {
  red: 'critical',
  watch: 'warning',
  green: 'ok',
};

const WATER_SEVERITY: Record<WaterStateValue, Severity> = {
  delivered: 'ok',
  partial: 'progress',
  none: 'warning',
  unmeasured: 'missing',
};

export const LAYER_SPECS: Record<ColorBy, LayerSpec> = {
  crop: {
    id: 'crop',
    stateKey: null,
    requires: null,
    valueOf: (props) => str(props.crop_2026) ?? LEGEND_NONE,
    severity: () => null,
    points: 'none',
  },
  landCategory: {
    id: 'landCategory',
    stateKey: null,
    requires: null,
    valueOf: (props) => str(props.category) ?? 'other',
    severity: () => null,
    points: 'none',
  },
  geometryStatus: {
    id: 'geometryStatus',
    stateKey: null,
    requires: null,
    valueOf: (props) => str(props.geometry_status) ?? LEGEND_NONE,
    severity: () => null,
    points: 'none',
  },
  workStatus: {
    id: 'workStatus',
    stateKey: 'ws',
    requires: 'workStatusById',
    valueOf: (props, data) => {
      const value = data.workStatusById?.[props.id];
      // В легенде «отчитались» одна позиция на оба вида отчёта.
      if (value === 'reported_verified') return 'reported';
      return value ?? LEGEND_NONE;
    },
    severity: (key) => WORK_SEVERITY[key as WorkStatusValue] ?? null,
    // Статус работы и есть содержание слоя: на отдалении видны все цвета.
    points: 'all',
    stateById: (data) => data.workStatusById,
  },
  quarantine: {
    id: 'quarantine',
    stateKey: 'q',
    requires: 'quarantineById',
    // Умолчание слоя — зелёный: «событий не было», а не «проверено».
    valueOf: (props, data) => data.quarantineById?.[props.id] ?? 'green',
    severity: (key) => QUARANTINE_SEVERITY[key as QuarantineStatusValue] ?? null,
    // Зелёный — состояние по умолчанию, тысячи его точек затопили бы район.
    points: 'attention',
    stateById: (data) => data.quarantineById,
  },
  water: {
    id: 'water',
    stateKey: 'v',
    requires: 'waterById',
    valueOf: (props, data) => data.waterById?.[props.id] ?? LEGEND_NONE,
    severity: (key) => WATER_SEVERITY[key as WaterStateValue] ?? null,
    points: 'attention',
    stateById: (data) => data.waterById,
  },
  values: {
    id: 'values',
    stateKey: 'v',
    requires: 'values',
    valueOf: (props, data) => data.values?.byId[props.id] ?? LEGEND_NONE,
    severity: (key, data) => {
      const tone = data.values?.legend.find((entry) => entry.key === key)?.tone;
      // Верх шкалы частоты — очаг: точка на отдалении, иначе мелкие контуры
      // с 10 предписаниями не видны на районном зуме.
      if (tone === 'heat-5' || tone === 'heat-4') return 'critical';
      return tone && (SEVERITY_ORDER as readonly string[]).includes(tone) ? (tone as Severity) : null;
    },
    points: 'attention',
    stateById: (data) => data.values?.byId,
  },
};

export function layerSpec(id: ColorBy): LayerSpec {
  return LAYER_SPECS[id];
}

/** Есть ли у слоя данные; слой без данных кадр показывает причиной, а не пятнами. */
export function layerHasData(id: ColorBy, data: MapLayerData): boolean {
  const need = LAYER_SPECS[id].requires;
  return need === null || data[need] !== undefined;
}

/* -------------------------------------------------- точки и кластеры */

export interface StatusPoint {
  id: string;
  center: [number, number];
  severity: Severity;
}

/** Попадает ли тяжесть в режим точек. */
export function wantsPoint(severity: Severity | null, mode: PointsMode): severity is Severity {
  if (severity === null || mode === 'none') return false;
  return mode === 'all' ? true : ATTENTION.includes(severity);
}

/**
 * Точки статуса для источника кластеров: по одной на контур, у которого
 * тяжесть попадает в режим. Контур без центра (геометрия не пришла) точки не
 * получает — это сказано в легенде счётом, а не молчанием.
 */
export function statusPoints(
  rows: ReadonlyArray<{ props: LayerFeatureProps; center: [number, number] | null }>,
  spec: LayerSpec,
  data: MapLayerData,
  mode: PointsMode,
  hidden: ReadonlySet<string> = new Set(),
): StatusPoint[] {
  const out: StatusPoint[] = [];
  for (const row of rows) {
    if (!row.center) continue;
    const key = spec.valueOf(row.props, data);
    if (hidden.has(key)) continue;
    const severity = spec.severity(key, data);
    if (!wantsPoint(severity, mode)) continue;
    out.push({ id: row.props.id, center: row.center, severity });
  }
  return out;
}

/**
 * Свойства кластера MapLibre: счёт точек каждой тяжести. Кластер красится
 * худшей из присутствующих — красный в кластере из сотни жёлтых остаётся
 * красным на любом масштабе.
 */
export function clusterProperties(): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const severity of SEVERITY_ORDER) {
    out[`n_${severity}`] = ['+', ['case', ['==', ['get', 'severity'], severity], 1, 0]];
  }
  return out;
}

/** Цвет кластера: первая по тяжести ступень с ненулевым счётом. */
export function clusterColorExpression(palette: Record<Severity, string> = SEVERITY_COLOR): unknown {
  const expr: unknown[] = ['case'];
  for (const severity of SEVERITY_ORDER) {
    expr.push(['>', ['get', `n_${severity}`], 0], palette[severity]);
  }
  expr.push(palette.missing);
  return expr;
}

/** Цвет одиночной точки по её тяжести. */
export function pointColorExpression(palette: Record<Severity, string> = SEVERITY_COLOR): unknown {
  const expr: unknown[] = ['match', ['get', 'severity']];
  for (const severity of SEVERITY_ORDER) expr.push(severity, palette[severity]);
  expr.push(palette.missing);
  return expr;
}

/** Худшая тяжесть из набора — то же правило, что у цвета кластера. */
export function worstSeverity(list: Iterable<Severity>): Severity | null {
  let best: number | null = null;
  for (const severity of list) {
    const rank = SEVERITY_ORDER.indexOf(severity);
    if (best === null || rank < best) best = rank;
  }
  return best === null ? null : SEVERITY_ORDER[best]!;
}

/* ------------------------------------------- фильтр слоя (легенда) */

/**
 * Скрытые значения легенды превращаются в набор id: фильтр MapLibre не умеет
 * читать feature-state, поэтому «спрятать жёлтые» считается здесь.
 * `null` — не сужать (ни базового фильтра, ни скрытых значений).
 */
export function visibleIds(
  features: ReadonlyArray<LayerFeatureProps>,
  spec: LayerSpec,
  data: MapLayerData,
  hidden: ReadonlySet<string>,
  base: ReadonlyArray<string> | null,
): string[] | null {
  if (hidden.size === 0) return base ? [...base] : null;
  const allow = base ? new Set(base) : null;
  const out: string[] = [];
  for (const props of features) {
    if (allow && !allow.has(props.id)) continue;
    if (hidden.has(spec.valueOf(props, data))) continue;
    out.push(props.id);
  }
  return out;
}

/** Значения, реально встреченные в выборке: легенда показывает то, что есть. */
export function presentValues(
  features: ReadonlyArray<LayerFeatureProps>,
  spec: LayerSpec,
  data: MapLayerData,
  base: ReadonlyArray<string> | null,
): Set<string> {
  const allow = base ? new Set(base) : null;
  const seen = new Set<string>();
  for (const props of features) {
    if (allow && !allow.has(props.id)) continue;
    seen.add(spec.valueOf(props, data));
  }
  return seen;
}

/* ------------------------------------------------ адаптеры данных */

/**
 * Строка ответа «статус работ по контурам» (`GET /season/contour-status`,
 * 6al.2: поле `status`). Форма терпимая: id контура числом или строкой;
 * прежняя форма `state` (значение `Obligation.state` фикстур) тоже читается.
 */
export interface ContourWorkStatusRow {
  contour_id: number | string;
  status?: string;
  state?: WorkStatusValue | string;
}

const WORK_STATES = new Set<string>(['reported_verified', 'reported', 'in_progress', 'in_window', 'upcoming', 'silent']);

/**
 * Статус сервера → значение слоя. «После срока» сервера — «без отчёта» слоя:
 * отчёта нет при закрытом окне, это про данные, а не про работу (нейтраль,
 * не красный). `no_obligations` строкой не приходит — контур без значения.
 */
const SERVER_WORK_STATUS: Record<string, WorkStatusValue> = {
  overdue: 'silent',
  in_window: 'in_window',
  awaiting_window: 'upcoming',
  in_progress: 'in_progress',
  reported: 'reported',
};

/**
 * Ответ эндпоинта → `workStatusById`. Незнакомое состояние пропускается, а не
 * красится «чем-нибудь»: контур без значения честно остаётся «обязательства
 * нет», пока словарь не согласован.
 */
export function workStatusByContour(
  rows: ReadonlyArray<ContourWorkStatusRow>,
): Record<string, WorkStatusValue> {
  const out: Record<string, WorkStatusValue> = {};
  for (const row of rows) {
    const value =
      row.status !== undefined
        ? SERVER_WORK_STATUS[row.status]
        : row.state !== undefined && WORK_STATES.has(row.state)
          ? (row.state as WorkStatusValue)
          : undefined;
    if (!value) continue;
    out[String(row.contour_id)] = value;
  }
  return out;
}

/** Строка распределения воды (`WaterAllocationOut`), только нужные поля. */
export interface WaterAllocationRow {
  contour_id: number | string;
  requested_volume_m3: string | number | null;
  volume_m3: string | number | null;
}

const num = (value: string | number | null): number | null => {
  if (value === null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Распределения полива → состояние подачи на контур. Строки одного контура
 * суммируются. «Подано» — не меньше 95 % запрошенного (допуск на округление
 * учёта), «частично» — больше нуля, «не подано» — ноль при запросе. Объём не
 * измерен ни в одной строке — «не измерено», а не ноль (отсутствие ≠ ноль).
 *
 * Запрос без числа тоже не ноль (6al.13): если хоть у одной строки контура
 * запрошенный объём не известен, сравнить подачу с запросом нельзя, и при
 * подаче больше нуля контур — «не измерено», а не «подано» или «частично».
 */
export function waterByAllocations(
  rows: ReadonlyArray<WaterAllocationRow>,
  /** Вода — события файла ирригации: заявок нет, контур с водой — «подано», без неё — «не подано». */
  events = false,
): Record<string, WaterStateValue> {
  const sums = new Map<string, { requested: number | null; delivered: number; measured: boolean }>();
  for (const row of rows) {
    const id = String(row.contour_id);
    const acc = sums.get(id) ?? { requested: 0, delivered: 0, measured: false };
    const requested = num(row.requested_volume_m3);
    acc.requested = acc.requested === null || requested === null ? null : acc.requested + requested;
    const volume = num(row.volume_m3);
    if (volume !== null) {
      acc.delivered += volume;
      acc.measured = true;
    }
    sums.set(id, acc);
  }
  const out: Record<string, WaterStateValue> = {};
  for (const [id, acc] of sums) {
    if (!acc.measured) out[id] = 'unmeasured';
    else if (acc.delivered <= 0) out[id] = 'none';
    else if (events) out[id] = 'delivered';
    else if (acc.requested === null) out[id] = 'unmeasured';
    else if (acc.requested > 0 && acc.delivered < acc.requested * 0.95) out[id] = 'partial';
    else out[id] = 'delivered';
  }
  return out;
}
